/**
 * Data for the Expenses section — the Reimburse app's collections, read and
 * written from the desk.
 *
 * Everything is fetched whole and filtered here. One user's expenses are a few
 * hundred rows a year; a full list is one request, gives the reports list its
 * totals without a query per report, and needs nothing from the demo mock's
 * filter support (which handles a single equality and nothing else).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { appMode, pb } from "@/lib/pocketbase";
import type { Expense, ExpenseCategory, ExpenseReport, MileageRate } from "@/lib/types";

const userId = () => (pb.authStore.model as { id?: string } | null)?.id;

export function useExpenseReports() {
  return useQuery({
    queryKey: ["expense_reports"],
    queryFn: () => pb.collection("expense_reports").getFullList<ExpenseReport>({ sort: "-created" }),
  });
}

export function useExpenses() {
  return useQuery({
    queryKey: ["expenses"],
    queryFn: () => pb.collection("expenses").getFullList<Expense>({ sort: "date,created" }),
  });
}

export function useExpenseCategories() {
  return useQuery({
    queryKey: ["expense_categories"],
    queryFn: () => pb.collection("expense_categories").getFullList<ExpenseCategory>({ sort: "sort" }),
  });
}

export function useMileageRates() {
  return useQuery({
    queryKey: ["mileage_rates"],
    queryFn: () => pb.collection("mileage_rates").getFullList<MileageRate>({ sort: "-effective_date" }),
  });
}

/** Where a receipt can be fetched from: the PocketBase file URL (through the
 *  same-origin /api proxy) live, the stored data URL in the demo. */
export function receiptUrl(e: Expense, name: string): string {
  if (appMode === "demo") return e.receiptData?.[name] ?? "";
  try { return (pb as unknown as { files: { getUrl: (r: unknown, n: string) => string } }).files.getUrl(e, name); }
  catch { return ""; }
}

/** PocketBase puts the useful part of a rejection in `response.data`. */
export function pbError(e: unknown): string {
  const err = e as { response?: { message?: string; data?: Record<string, { message?: string }> }; message?: string };
  const data = err?.response?.data;
  const detail = data && Object.entries(data).map(([k, v]) => `${k}: ${v?.message ?? v}`).join("; ");
  const base = err?.response?.message || err?.message || String(e);
  return detail ? `${base} (${detail})` : base;
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(r.error);
    r.readAsDataURL(file);
  });
}

/** The demo has no file store: keep the bytes on the record, as artifacts do. */
async function demoReceipts(prev: Expense | undefined, add: File[], remove: string[]) {
  const data: Record<string, string> = { ...(prev?.receiptData ?? {}) };
  let names = (prev?.receipts ?? []).filter((n) => !remove.includes(n));
  for (const n of remove) delete data[n];
  for (const f of add) {
    // PocketBase suffixes names to keep them unique; the demo does the same.
    const stem = f.name.replace(/\.[^.]+$/, "") || "receipt";
    const ext = f.name.includes(".") ? f.name.slice(f.name.lastIndexOf(".")) : "";
    const name = `${stem}_${Math.random().toString(36).slice(2, 8)}${ext}`;
    data[name] = await readAsDataUrl(f);
    names = [...names, name];
  }
  return { receipts: names, receiptData: data };
}

export interface SaveExpenseInput {
  id?: string;
  data: Partial<Expense>;
  /** New receipt files to attach. */
  add?: File[];
  /** Existing receipt file names to remove. */
  remove?: string[];
}

export function useExpenseMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["expenses"] });
    void qc.invalidateQueries({ queryKey: ["expense_reports"] });
  };

  const saveExpense = useMutation({
    mutationFn: async ({ id, data, add = [], remove = [] }: SaveExpenseInput): Promise<Expense> => {
      const col = pb.collection("expenses");
      if (appMode === "demo") {
        const prev = id ? await col.getOne<Expense>(id) : undefined;
        const files = add.length || remove.length ? await demoReceipts(prev, add, remove) : {};
        return id
          ? col.update<Expense>(id, { ...data, ...files })
          : col.create<Expense>({ ...data, ...files, user: userId() });
      }
      const body: Record<string, unknown> = id ? { ...data } : { ...data, user: userId() };
      // PocketBase removes named files from a multi-file field via `field-`.
      if (remove.length) body["receipts-"] = remove;
      if (!add.length) return id ? col.update<Expense>(id, body) : col.create<Expense>(body);
      const fd = new FormData();
      for (const [k, v] of Object.entries(body)) {
        if (v === undefined || v === null) continue;
        if (Array.isArray(v)) v.forEach((x) => fd.append(k, String(x)));
        else fd.append(k, String(v));
      }
      for (const f of add) fd.append("receipts", f);
      return id ? col.update<Expense>(id, fd) : col.create<Expense>(fd);
    },
    onSuccess: invalidate,
  });

  const deleteExpense = useMutation({
    mutationFn: (id: string) => pb.collection("expenses").delete(id),
    onSuccess: invalidate,
  });

  /** One write per record — PocketBase has no batch update here. */
  const setFlag = useMutation({
    mutationFn: async ({ ids, field, value }: { ids: string[]; field: "added_to_quicken" | "added_to_invoice" | "receipt_captured"; value: boolean }) => {
      for (const id of ids) await pb.collection("expenses").update(id, { [field]: value });
    },
    onSuccess: invalidate,
  });

  return { saveExpense, deleteExpense, setFlag };
}

export function useReportMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["expense_reports"] });
    void qc.invalidateQueries({ queryKey: ["expenses"] });
  };

  const saveReport = useMutation({
    mutationFn: ({ id, ...data }: Partial<ExpenseReport> & { id?: string }) =>
      id
        ? pb.collection("expense_reports").update<ExpenseReport>(id, data)
        : pb.collection("expense_reports").create<ExpenseReport>({ status: "active", ...data, user: userId() }),
    onSuccess: invalidate,
  });

  /**
   * `expenses.report` does not cascade in the schema, so the children go first
   * or they are left as rows nothing can reach.
   */
  const deleteReport = useMutation({
    mutationFn: async ({ id, expenseIds }: { id: string; expenseIds: string[] }) => {
      for (const e of expenseIds) await pb.collection("expenses").delete(e);
      await pb.collection("expense_reports").delete(id);
    },
    onSuccess: invalidate,
  });

  return { saveReport, deleteReport };
}

export function useCategoryMutations() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["expense_categories"] });

  const saveCategory = useMutation({
    mutationFn: ({ id, ...data }: Partial<ExpenseCategory> & { id?: string }) =>
      id
        ? pb.collection("expense_categories").update<ExpenseCategory>(id, data)
        : pb.collection("expense_categories").create<ExpenseCategory>({ archived: false, ...data, user: userId() }),
    onSuccess: invalidate,
  });

  /** Renumber 1..n in the given order, writing only what moved. */
  const reorder = useMutation({
    mutationFn: async (ordered: ExpenseCategory[]) => {
      for (let n = 0; n < ordered.length; n++) {
        if (ordered[n].sort !== n + 1) await pb.collection("expense_categories").update(ordered[n].id, { sort: n + 1 });
      }
    },
    onSuccess: invalidate,
  });

  const deleteCategory = useMutation({
    mutationFn: (id: string) => pb.collection("expense_categories").delete(id),
    onSuccess: invalidate,
  });

  return { saveCategory, reorder, deleteCategory };
}

export function useRateMutations() {
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["mileage_rates"] });

  const saveRate = useMutation({
    mutationFn: ({ id, ...data }: Partial<MileageRate> & { id?: string }) =>
      id
        ? pb.collection("mileage_rates").update<MileageRate>(id, data)
        : pb.collection("mileage_rates").create<MileageRate>({ note: "", ...data, user: userId() }),
    onSuccess: invalidate,
  });

  const deleteRate = useMutation({
    mutationFn: (id: string) => pb.collection("mileage_rates").delete(id),
    onSuccess: invalidate,
  });

  return { saveRate, deleteRate };
}

/** Hand a CSV to the browser as a download. */
export function downloadCsv(csv: string, filename: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
